import { Component, ContentChildren, EventEmitter, Input, OnInit, Output, QueryList, ViewChild } from '@angular/core';
import { AbstractControl, FormArray, FormGroup } from '@angular/forms';
import { Sort } from '@angular/material/sort';
import { MatColumnDef, MatTable, MatTableDataSource } from '@angular/material/table';
import { Subscription } from 'rxjs';

@Component({
    selector: 'form-array-table',
    templateUrl: './form-array-table.component.html',
    styleUrls: ['./form-array-table.component.scss'],
})
export class FormArrayTableComponent implements OnInit {
    @Input() displayedColumns: string[] = [];
    @Input()
    get data(): FormArray<any> {
        return this._data;
    }
    set data(data: FormArray<any>) {
        this.handleDataChange(data);
    }
    @Input() options: FormArrayTableOptions;
    @Output() onRemoveRowEvent = new EventEmitter<string>();
    @Output() onMoveRowEvent = new EventEmitter<FormArrayTableRowMoveEvent>();
    @Output() onRefreshRowEvent = new EventEmitter<string>();
    @Output() onValueChangesEvent = new EventEmitter<string>();
    @ViewChild(MatTable, { static: true }) table: MatTable<any>;
    @ContentChildren(MatColumnDef) columnDefs: QueryList<MatColumnDef>;
    private _data: FormArray<any>;
    private subscriptions: Subscription[] = [];
    protected dataSource: MatTableDataSource<AbstractControl<any, any>>;

    constructor() {}

    ngOnInit(): void {}

    ngOnDestroy(): void {
        this.subscriptions.forEach((subscription) => subscription.unsubscribe());
    }

    // Need to add the projected columns to the table manually
    ngAfterContentInit() {
        this.columnDefs.forEach((columnDef) => this.table.addColumnDef(columnDef));

        if (this.options.includeRowNumbers) {
            this.displayedColumns.unshift('no');
        }

        // Add the actions column if any actions are allowed
        if (this.options.allowRowDeletion) {
            this.displayedColumns.push('actions');
        }
    }

    handleDataChange(data: FormArray<any>) {
        // Got new data, so we reset all subscriptions
        this.subscriptions.forEach((subscription) => subscription.unsubscribe());

        this._data = data;
        this.dataSource = new MatTableDataSource(this.data.controls);
        data.controls.forEach((control) => {
            let subscription = control.valueChanges.subscribe(() => {
                this.onValueChangesEvent.emit();
            });
            this.subscriptions.push(subscription);
        });
        // Trigger value changes once after new data is received
        this.onValueChangesEvent.emit();
    }

    applySort(sort: Sort) {
        let controls = this.dataSource.data;
        let sortedData = controls.sort((a, b) => {
            const valueA = a.get(sort.active).value;
            const valueB = b.get(sort.active).value;

            if (!sort.active || sort.direction === '') {
                let defaultSortColumn = this.options.defaultSortColumn;
                const valueAIndex = this.data.controls.findIndex(
                    (control) => control.get(defaultSortColumn) == a.get(defaultSortColumn).value
                );
                const valueBIndex = this.data.controls.findIndex(
                    (control) => control.get(defaultSortColumn) == b.get(defaultSortColumn).value
                );
                return valueAIndex < valueBIndex ? -1 : 1;
            }

            if (valueA < valueB) {
                return sort.direction === 'asc' ? -1 : 1;
            } else if (valueA > valueB) {
                return sort.direction === 'asc' ? 1 : -1;
            } else {
                return 0;
            }
        });

        this.dataSource = new MatTableDataSource(sortedData);
    }

    onRemoveRow(element: any) {
        this.onRemoveRowEvent.emit(element);
    }

    onMoveRow(element: any, direction: 'up' | 'down') {
        this.onMoveRowEvent.emit({
            element,
            direction,
        } as FormArrayTableRowMoveEvent);
    }

    onRefreshRow(element: any) {
        this.onRefreshRowEvent.emit(element);
    }
}

export interface FormArrayTableOptions {
    allowRowDeletion?: boolean;
    allowRowReorder?: boolean;
    allowRowRefresh?: boolean;
    defaultSortColumn?: string;
    includeRowNumbers?: boolean;
    includeFooterRow?: boolean;
}

export interface FormArrayTableRowMoveEvent {
    element: FormGroup;
    direction: 'up' | 'down';
}
